import React, { useState, useEffect } from 'react';
import { Crown, Plus, Download, Upload, Search, Edit2, Trash2, X, AlertCircle, CheckCircle } from 'lucide-react';
import { vipApi } from '../../services/ivrApi';
import OrganizationFilter from '../../components/Filters/OrganizationFilter';
import { useScopedOrgId } from '../../hooks/useScopedOrgId';
import type { VIPCaller } from '../../types/ivr';

// ============================================================================
// VIP Management Page
// ============================================================================

const VIPManagement: React.FC = () => {
  const [vipCallers, setVipCallers] = useState<VIPCaller[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVIP, setEditingVIP] = useState<VIPCaller | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  // Business admins get their own organization implicitly; provider-level roles
  // choose one. See useScopedOrgId — before it, every role outside the chooser
  // list was left with no organization at all and a dead page.
  const { selectedOrgId, setSelectedOrgId, showOrgFilter } = useScopedOrgId();

  useEffect(() => {
    if (selectedOrgId) {
      loadVIPCallers();
    } else {
      setVipCallers([]);
    }
  }, [selectedOrgId, currentPage]);

  const loadVIPCallers = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    try {
      // Use search endpoint for pagination support
      const response = await vipApi.search(selectedOrgId, {
        page: currentPage - 1, // Backend uses 0-indexed pages
        size: itemsPerPage,
      });

      if (response.success && response.data) {
        setVipCallers(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        // Fallback to getAll if search fails
        const allResponse = await vipApi.getAll(selectedOrgId);
        if (allResponse.success && allResponse.data) {
          setVipCallers(allResponse.data);
          setTotalPages(1);
        } else {
          showNotification('error', 'Failed to load VIP callers');
        }
      }
    } catch (error) {
      showNotification('error', 'Network error loading VIP callers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVIP = async (vip: Partial<VIPCaller>) => {
    try {
      // Transform frontend VIPCaller fields to backend expected fields
      const vipData = {
        phoneNumber: vip.msisdn || '',
        vipName: vip.notes || undefined,
        priorityLevel: vip.vip_level || 1,
        customGreeting: vip.custom_entry_state || undefined,
        notes: vip.notes || undefined,
        enabled: vip.is_active !== false,
      };

      const response = editingVIP
        ? await vipApi.update(selectedOrgId, editingVIP.id!, vipData)
        : await vipApi.create(selectedOrgId, vipData);

      if (response.success) {
        await loadVIPCallers();
        setShowModal(false);
        setEditingVIP(null);
        showNotification(
          'success',
          editingVIP ? 'VIP caller updated successfully' : 'VIP caller added successfully'
        );
      } else {
        showNotification('error', response.error || 'Failed to save VIP caller');
      }
    } catch (error) {
      showNotification('error', 'Network error saving VIP caller');
    }
  };

  const handleDeleteVIP = async (id: number, msisdn: string) => {
    if (confirm(`Remove ${msisdn} from VIP list?`)) {
      try {
        const response = await vipApi.delete(selectedOrgId, id);
        if (response.success) {
          await loadVIPCallers();
          showNotification('success', 'VIP caller removed successfully');
        } else {
          showNotification('error', response.error || 'Failed to remove VIP caller');
        }
      } catch (error) {
        showNotification('error', 'Network error removing VIP caller');
      }
    }
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedOrgId) return;

    setLoading(true);
    try {
      const response = await vipApi.bulkImport(selectedOrgId, file);
      if (response.success) {
        await loadVIPCallers();
        showNotification('success', `Imported ${response.data?.imported || 0} VIP callers`);
      } else {
        showNotification('error', response.error || 'Bulk import failed');
      }
    } catch (error) {
      showNotification('error', 'Network error during bulk import');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleExport = async () => {
    if (!selectedOrgId) return;

    try {
      const blob = await vipApi.export(selectedOrgId);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vip_callers_${selectedOrgId}_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showNotification('success', 'VIP list exported successfully');
      }
    } catch (error) {
      showNotification('error', 'Failed to export VIP list');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const filteredVIPs = vipCallers.filter(
    vip =>
      vip.msisdn.includes(searchTerm) ||
      vip.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vip.custom_entry_state?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Crown className="w-6 h-6 text-yellow-500" />
            <span>VIP Caller Management</span>
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage priority routing for VIP callers with custom entry states
          </p>
        </div>
        <button
          onClick={() => {
            setEditingVIP(null);
            setShowModal(true);
          }}
          disabled={!selectedOrgId}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Plus className="w-4 h-4" />
          <span>Add VIP Caller</span>
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`flex items-center space-x-2 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-400'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Filter - Only for admin roles */}
      {showOrgFilter && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <OrganizationFilter
            value={selectedOrgId}
            onChange={(value) => {
              setSelectedOrgId(value);
              setCurrentPage(1); // Reset pagination when business changes
            }}
            className="w-full md:w-96"
            requireSelection={true}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show message if no org selected */}
      {showOrgFilter && !selectedOrgId && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
          <p className="text-blue-800 dark:text-blue-400 text-center">
            Please select a business from the dropdown above to view VIP callers.
          </p>
        </div>
      )}

      {selectedOrgId && (
        <>
          {/* Actions Bar */}
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by phone number, notes, or entry state..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <Upload className="w-4 h-4" />
                <span>Import CSV</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkImport}
                  className="hidden"
                />
              </label>
              <button
                onClick={handleExport}
                disabled={vipCallers.length === 0}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* VIP List Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Loading VIP callers...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        VIP Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Custom Entry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Transfer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredVIPs.map(vip => (
                      <tr key={vip.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{vip.msisdn}</div>
                          {vip.notes && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {vip.notes}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              vip.vip_level === 1
                                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400'
                                : vip.vip_level === 2
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400'
                                : vip.vip_level === 3
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400'
                                : 'bg-pink-100 dark:bg-pink-900/40 text-pink-800 dark:text-pink-400'
                            }`}
                          >
                            <Crown className="w-3 h-3 mr-1" />
                            Level {vip.vip_level}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {vip.priority || 100}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {vip.custom_entry_state || '-'}
                          </span>
                          {vip.bypass_subscription_check && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-400">
                              Bypass Sub
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {vip.direct_transfer_number || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              vip.is_active
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                            }`}
                          >
                            {vip.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingVIP(vip);
                              setShowModal(true);
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-3 transition"
                            title="Edit VIP caller"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteVIP(vip.id!, vip.msisdn)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition"
                            title="Remove VIP caller"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredVIPs.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Crown className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm">
                      {searchTerm ? 'No VIP callers found matching your search' : 'No VIP callers added yet'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <VIPModal
          vip={editingVIP}
          businessId={selectedOrgId}
          onSave={handleSaveVIP}
          onClose={() => {
            setShowModal(false);
            setEditingVIP(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// VIP Modal Component
// ============================================================================

interface VIPModalProps {
  vip: VIPCaller | null;
  businessId: string;
  onSave: (vip: Partial<VIPCaller>) => void;
  onClose: () => void;
}

const VIPModal: React.FC<VIPModalProps> = ({ vip, businessId, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<VIPCaller>>(
    vip || {
      msisdn: '',
      vip_level: 1,
      priority: 100,
      bypass_subscription_check: false,
      is_active: true,
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate phone number (E.164 format)
    if (!formData.msisdn) {
      newErrors.msisdn = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{10,14}$/.test(formData.msisdn)) {
      newErrors.msisdn = 'Invalid phone number format (E.164)';
    }

    // Validate VIP level
    if (!formData.vip_level || formData.vip_level < 1 || formData.vip_level > 4) {
      newErrors.vip_level = 'VIP level must be between 1 and 4';
    }

    // Validate priority
    if (formData.priority !== undefined && (formData.priority < 1 || formData.priority > 1000)) {
      newErrors.priority = 'Priority must be between 1 and 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            <span>{vip ? 'Edit VIP Caller' : 'Add VIP Caller'}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number (E.164) <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="+1234567890"
              value={formData.msisdn}
              onChange={e => setFormData({ ...formData, msisdn: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                errors.msisdn ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.msisdn && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.msisdn}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Format: +[country code][number] (e.g., +12025551234)
            </p>
          </div>

          {/* VIP Level and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                VIP Level <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.vip_level}
                onChange={e =>
                  setFormData({ ...formData, vip_level: parseInt(e.target.value) as 1 | 2 | 3 | 4 })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white ${
                  errors.vip_level ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value={1}>Level 1 (Highest)</option>
                <option value={2}>Level 2</option>
                <option value={3}>Level 3</option>
                <option value={4}>Level 4</option>
              </select>
              {errors.vip_level && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.vip_level}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority (1-1000)
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={formData.priority || 100}
                onChange={e => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white ${
                  errors.priority ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.priority && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.priority}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Lower = higher priority</p>
            </div>
          </div>

          {/* Custom Entry State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom Entry State
            </label>
            <input
              type="text"
              placeholder="e.g., VIP_MENU, PRIORITY_SUPPORT"
              value={formData.custom_entry_state || ''}
              onChange={e => setFormData({ ...formData, custom_entry_state: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              IVR state where VIP callers should be routed (leave empty for default)
            </p>
          </div>

          {/* Direct Transfer Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Direct Transfer Number
            </label>
            <input
              type="tel"
              placeholder="+1234567890 or SIP/agent123"
              value={formData.direct_transfer_number || ''}
              onChange={e => setFormData({ ...formData, direct_transfer_number: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Directly transfer VIP caller to this number (bypasses IVR menu)
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea
              placeholder="Additional notes about this VIP caller..."
              value={formData.notes || ''}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.bypass_subscription_check || false}
                onChange={e =>
                  setFormData({ ...formData, bypass_subscription_check: e.target.checked })
                }
                className="mt-1 w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 dark:bg-gray-700"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Bypass Subscription Check
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Allow VIP caller to access IVR even if not subscribed
                </div>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active !== false}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="mt-1 w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 dark:bg-gray-700"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Active</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  VIP status is currently active
                </div>
              </div>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition bg-white dark:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
            >
              <Crown className="w-4 h-4" />
              <span>{vip ? 'Update VIP Caller' : 'Add VIP Caller'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VIPManagement;
